import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import ClientPicker from "@/components/ClientPicker";
import MongoSteps from "@/components/mongo/MongoSteps";
import {
    FlaskConical, Play, Wrench, Sparkles, Trash2, Plus, ChevronDown, ChevronRight,
    Clock, DollarSign, FileJson, Save,
} from "lucide-react";

/**
 * AgentPlayground — interactive runner for a single agent.
 *
 * Left side: input editor (free-form JSON) + tenancy fields + tool context loaders.
 * Right side: latest run output + tokens/latency/cost.
 */
export default function AgentPlayground({ agent, tools }) {
    const [inputJson, setInputJson] = useState(() => {
        const sample = sampleFromSchema(agent.input_schema);
        return JSON.stringify(sample, null, 2);
    });
    const [attachments, setAttachments] = useState([]);
    const [clientId, setClientId] = useState("");
    const [siteId, setSiteId] = useState("");
    const [studyId, setStudyId] = useState("");
    const [running, setRunning] = useState(false);
    const [run, setRun] = useState(null);
    const [error, setError] = useState(null);
    const [saveAs, setSaveAs] = useState("");

    const enabledTools = useMemo(
        () => tools.filter((t) => (agent.tool_access || []).includes(t.name)),
        [tools, agent.tool_access],
    );

    const execute = async () => {
        setError(null);
        let payload;
        try {
            payload = JSON.parse(inputJson || "{}");
        } catch (e) {
            return setError(`Invalid input JSON: ${e.message}`);
        }
        setRunning(true);
        try {
            const { data } = await api.post(`/admin/agents/${agent.id}/run`, {
                input_payload: payload,
                context_attachments: attachments,
                client_id: clientId || null,
                site_id: siteId || null,
                study_id: studyId || null,
                save_as_test_case: saveAs.trim() || null,
            });
            setRun(data);
            if (saveAs.trim()) {
                toast.success(`Run + test case "${saveAs.trim()}" saved`);
                setSaveAs("");
            } else {
                toast.success("Run completed");
            }
        } catch (e) {
            const detail = formatApiErrorDetail(e?.response?.data?.detail) || e.message;
            setError(detail);
            toast.error(detail);
        } finally {
            setRunning(false);
        }
    };

    const removeAttachment = (idx) => {
        setAttachments(attachments.filter((_, i) => i !== idx));
    };

    return (
        <div className="grid grid-cols-2 gap-5 mt-5" data-testid="agent-playground">
            {/* LEFT — input + tools */}
            <div className="space-y-4">
                <div className="border border-white/10 bg-[#0a0a0a] rounded-md p-5 space-y-3">
                    <div className="label-xs text-white/40 flex items-center gap-1.5">
                        <FileJson className="w-3.5 h-3.5" /> Input payload
                    </div>
                    <textarea
                        value={inputJson}
                        onChange={(e) => setInputJson(e.target.value)}
                        rows={10}
                        data-testid="playground-input"
                        className="input font-mono text-[11px]"
                    />
                    <div className="text-[10px] text-white/40">
                        Available placeholders inside the user-prompt template: any top-level field of this object as <code className="px-1 bg-white/5 rounded text-[#7C3AED]">{`{{field}}`}</code>, plus <code className="px-1 bg-white/5 rounded text-[#7C3AED]">{`{{context}}`}</code> for attached tool results.
                    </div>
                </div>

                <div className="border border-white/10 bg-[#0a0a0a] rounded-md p-5 space-y-3">
                    <div className="label-xs text-white/40 flex items-center gap-1.5">
                        <Wrench className="w-3.5 h-3.5" /> Context loaders ({enabledTools.length})
                    </div>
                    {enabledTools.length === 0 ? (
                        <div className="text-[11px] text-white/40">No tools enabled. Add some on the Config tab.</div>
                    ) : (
                        <div className="space-y-2">
                            {enabledTools.map((t) => (
                                <ToolInvoker
                                    key={t.name}
                                    tool={t}
                                    onAttach={(att) => setAttachments([...attachments, att])}
                                />
                            ))}
                        </div>
                    )}
                    {attachments.length > 0 && (
                        <div className="pt-3 border-t border-white/10 space-y-1.5">
                            <div className="label-xs text-white/40">Attached context ({attachments.length})</div>
                            {attachments.map((a, i) => (
                                <div key={i} className="flex items-center gap-2 bg-[#050505] border border-white/10 rounded-md px-2 py-1.5 text-xs"
                                    data-testid={`playground-attachment-${i}`}>
                                    <Sparkles className="w-3 h-3 text-[#7C3AED]" />
                                    <span className="font-mono text-[11px]">{a.label || a.tool}</span>
                                    <span className="text-[10px] text-white/40 truncate max-w-[200px]">{summary(a.data)}</span>
                                    <button onClick={() => removeAttachment(i)} className="ml-auto text-white/40 hover:text-red-400">
                                        <Trash2 className="w-3 h-3" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="border border-white/10 bg-[#0a0a0a] rounded-md p-5 space-y-3">
                    <div className="label-xs text-white/40">Tenancy (optional, recorded on the run for billing)</div>
                    <div className="grid grid-cols-3 gap-2">
                        <ClientPicker
                            value={clientId}
                            onChange={setClientId}
                            compact
                            width="w-full"
                            anyOptionLabel="(any client)"
                            testId="playground-client-id"
                        />
                        <input value={siteId} onChange={(e) => setSiteId(e.target.value)} placeholder="Site ID"
                            data-testid="playground-site-id" className="input text-xs font-mono" />
                        <input value={studyId} onChange={(e) => setStudyId(e.target.value)} placeholder="Study ID"
                            data-testid="playground-study-id" className="input text-xs font-mono" />
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    <input value={saveAs} onChange={(e) => setSaveAs(e.target.value)} placeholder="Save as test case (optional name)"
                        data-testid="playground-save-as"
                        className="input text-xs flex-1" />
                    <button
                        onClick={execute}
                        disabled={running}
                        data-testid="playground-run"
                        className="px-4 py-2 bg-[#7C3AED] text-white rounded-md text-sm font-medium hover:bg-[#6D28D9] disabled:opacity-50 flex items-center gap-2"
                    >
                        <Play className="w-4 h-4" /> {running ? "Running…" : "Run"}
                    </button>
                </div>

                {error && (
                    <div className="text-xs text-red-400 border border-red-400/30 bg-red-400/5 rounded-md p-3" data-testid="playground-error">
                        {error}
                    </div>
                )}
            </div>

            {/* RIGHT — output */}
            <div className="space-y-4">
                {!run ? (
                    <div className="border border-dashed border-white/10 rounded-md p-10 text-center text-white/40 text-sm">
                        Run the agent to see output here.
                    </div>
                ) : (
                    <RunOutput run={run} />
                )}
            </div>
        </div>
    );
}

function ToolInvoker({ tool, onAttach }) {
    const [open, setOpen] = useState(false);
    const [params, setParams] = useState({});
    const [busy, setBusy] = useState(false);
    const fields = Object.entries(tool.params_schema?.properties || {});

    const invoke = async () => {
        setBusy(true);
        try {
            const cleaned = {};
            for (const [k, v] of Object.entries(params)) {
                if (v !== "" && v != null) cleaned[k] = v;
            }
            const { data } = await api.post(`/admin/agents/tools/invoke`, { name: tool.name, params: cleaned });
            const result = data.result || {};
            if (!result.ok) {
                toast.error(`Tool error: ${result.error || "unknown"}`);
            } else {
                onAttach({ tool: tool.name, label: tool.label, data: result });
                toast.success(`Attached ${tool.name}`);
                setOpen(false);
                setParams({});
            }
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="border border-white/10 bg-[#050505] rounded-md">
            <button onClick={() => setOpen(!open)} data-testid={`tool-${tool.name}`}
                className="w-full px-3 py-2 flex items-center justify-between text-xs hover:bg-white/[0.02]">
                <div className="flex items-center gap-2">
                    {open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                    <span className="font-mono">{tool.name}</span>
                </div>
                <span className="text-[10px] text-white/40">{tool.category}</span>
            </button>
            {open && (
                <div className="px-3 py-2 border-t border-white/10 space-y-2">
                    <div className="text-[10px] text-white/40">{tool.description}</div>
                    {fields.map(([k, spec]) => (
                        <div key={k}>
                            <div className="label-xs">{spec.label || k}{spec.required && <span className="text-red-400 ml-1">*</span>}</div>
                            {spec.type === "object" ? (
                                <textarea
                                    value={typeof params[k] === "object" ? JSON.stringify(params[k]) : params[k] || ""}
                                    onChange={(e) => {
                                        try { setParams({ ...params, [k]: JSON.parse(e.target.value) }); }
                                        catch { setParams({ ...params, [k]: e.target.value }); }
                                    }}
                                    rows={2} className="input font-mono text-[10px]" placeholder="{}"
                                />
                            ) : (
                                <input
                                    value={params[k] ?? ""}
                                    onChange={(e) => setParams({ ...params, [k]: spec.type === "integer" ? Number(e.target.value) : e.target.value })}
                                    type={spec.type === "integer" ? "number" : "text"}
                                    placeholder={spec.examples ? spec.examples.join(", ") : ""}
                                    className="input text-[11px]" data-testid={`tool-${tool.name}-${k}`}
                                />
                            )}
                        </div>
                    ))}
                    <button onClick={invoke} disabled={busy} data-testid={`tool-${tool.name}-invoke`}
                        className="w-full px-3 py-1.5 bg-[#7C3AED] text-white rounded-md text-xs font-medium hover:bg-[#6D28D9] disabled:opacity-50">
                        {busy ? "Loading…" : "Run & attach"}
                    </button>
                </div>
            )}
        </div>
    );
}

function RunOutput({ run }) {
    return (
        <>
            <div className="grid grid-cols-4 gap-3">
                <Stat label="Status" value={run.status} color={run.status === "completed" ? "text-emerald-400" : "text-red-400"} />
                <Stat label="Latency" value={`${run.latency_ms} ms`} icon={<Clock className="w-3 h-3" />} />
                <Stat label="Tokens" value={`${run.tokens_in}/${run.tokens_out}`} />
                <Stat label="Cost" value={`$${(run.total_cost_usd || 0).toFixed(5)}`} icon={<DollarSign className="w-3 h-3" />} />
            </div>
            {run.error && (
                <div className="text-xs text-red-400 border border-red-400/30 bg-red-400/5 rounded-md p-3">
                    {run.error}
                </div>
            )}
            {run.mongo_tools && (
                <div className="border border-white/10 bg-[#0a0a0a] rounded-md p-4 space-y-2" data-testid="playground-mongo">
                    <div className="text-[11px] font-mono text-white/50" data-testid="playground-mongo-lock">
                        MongoDB · {run.mongo_lock}{run.mongo_tools_error ? ` · unavailable: ${run.mongo_tools_error}` : ""}
                    </div>
                    <MongoSteps steps={run.tool_invocations} title="MongoDB lookups" testId="playground-mongo-steps" />
                </div>
            )}
            <div className="border border-white/10 bg-[#0a0a0a] rounded-md p-4 space-y-2">
                <div className="label-xs text-white/40">Output</div>
                <pre className="bg-[#050505] border border-white/10 rounded-md p-3 text-[11px] font-mono whitespace-pre-wrap max-h-[500px] overflow-auto"
                    data-testid="playground-output">
                    {run.output_payload
                        ? JSON.stringify(run.output_payload, null, 2)
                        : (run.output_text || run.raw_response || "—")}
                </pre>
            </div>
            <details className="border border-white/10 bg-[#0a0a0a] rounded-md p-4">
                <summary className="text-xs text-white/60 cursor-pointer">Raw response</summary>
                <pre className="mt-2 bg-[#050505] border border-white/10 rounded-md p-3 text-[10px] font-mono whitespace-pre-wrap max-h-60 overflow-auto">
                    {run.raw_response || "—"}
                </pre>
            </details>
        </>
    );
}

function Stat({ label, value, icon, color }) {
    return (
        <div className="border border-white/10 rounded-md p-3 bg-[#050505]">
            <div className="label-xs text-white/40 flex items-center gap-1.5">{icon} {label}</div>
            <div className={`font-mono text-sm mt-0.5 ${color || ""}`}>{value}</div>
        </div>
    );
}

function summary(data) {
    if (!data) return "";
    if (typeof data === "string") return data.slice(0, 60);
    return JSON.stringify(data).slice(0, 60);
}

function sampleFromSchema(schema) {
    if (!schema || typeof schema !== "object") return {};
    if (schema.type !== "object" || !schema.properties) return {};
    const out = {};
    for (const [k, v] of Object.entries(schema.properties)) {
        if (v.example !== undefined) out[k] = v.example;
        else if (v.type === "string") out[k] = "";
        else if (v.type === "number" || v.type === "integer") out[k] = 0;
        else if (v.type === "boolean") out[k] = false;
        else if (v.type === "array") out[k] = [];
        else if (v.type === "object") out[k] = {};
    }
    return out;
}
